import SnackBar from 'design-system/SnackBar/SnackBar';
import Toast from 'design-system/SnackBar/Toast/Toast';
import first from 'lodash-es/first';
import isNil from 'lodash-es/isNil';
import { useCallback, useEffect } from 'react';
import { connect, type ConnectedProps, useDispatch } from 'react-redux';
import * as NotificationsService from 'services/App/Notifications/actions';
import type { Notification, NotificationEventDetail, NotificationOld } from 'types/common/notification';
import type { ReduxDispatch, ReduxStateProps } from 'types/services/redux';
import manageError from 'utils/logging/manageError';

const mapStateToProps = (state: ReduxStateProps) => ({
  oldNotifications: state.notificationsReducer.notifications,
});

const mapDispatchToProps = (dispatch: ReduxDispatch) => ({
  removeNotification: (id: string) => dispatch(NotificationsService.deleteNotification(id)),
  clearNotification: () => dispatch(NotificationsService.clearNotification()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type NotificationProps = ConnectedProps<typeof connector> & { notifications: Notification[] };

const convertActionsToOld = (actions: Notification['actions']): NotificationOld['action'] => {
  if (!isNil(actions)) {
    if (actions.length > 1) {
      manageError(new Error(`Multiple actions in notification not supported yet, only the first one is used.`));
    }

    const action = first(actions);

    if (!isNil(action)) {
      return { label: action.content, action: action.action };
    }
  }

  return undefined;
};

const convertNotificationToOld = ({ id, actions, content, title, type }: Notification): NotificationOld => ({
  id,
  action: convertActionsToOld(actions),
  message: content,
  title,
  type,
});

const Notifications = ({ oldNotifications, removeNotification, clearNotification, notifications }: NotificationProps) => {
  const dispatch: ReduxDispatch = useDispatch();
  useEffect(() => {
    clearNotification();
  }, []);

  const showNotification = useCallback(
    (ev: CustomEvent<NotificationEventDetail>) => {
      const notification = notifications.find(({ id }) => id === ev.detail.id);

      if (!isNil(notification)) {
        dispatch({ type: 'CREATE_NOTIFICATION', notification: convertNotificationToOld(notification) });
      } else {
        manageError(new Error(`Unknown notification ${ev.detail.id}`));
      }
    },
    [dispatch, notifications],
  );

  useEffect(() => {
    document.addEventListener('notification', showNotification);

    return () => {
      document.removeEventListener('notification', showNotification);
    };
  }, [showNotification]);

  return (
    <SnackBar vertical="bottom" horizontal="center">
      {oldNotifications.map((notification) => (
        <Toast
          exitDirection="bottom"
          key={notification.id}
          id={notification.id}
          timeout={1000}
          containerSize={notification.containerSize}
          message={notification.message}
          action={notification.action}
          title={notification.title}
          type={notification.type}
          close={() => {
            removeNotification(notification.id);
          }}
        />
      ))}
    </SnackBar>
  );
};

export default connector(Notifications);
